import type {
  Parameter,
  ParameterExclusionOption,
} from "../../../models/AmlParameter";
import BodySmall from "../../atoms/typography/BodySmall";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Row } from "react-grid-system";
import DatePicker from "../filters/DatePicker";
import Checkbox from "../../atoms/inputs/Checkbox";
import TextField from "../../atoms/inputs/TextField";
import { useState } from "react";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ClockIconSmall } from "../../atoms/icons/ClockIcons";
import { useCreateMonitorExclusion } from "../../../graphql/hooks/aml/useCreateMonitorExclusion";
import { useTranslation } from "react-i18next";
import Alert from "../Alert";
import Divider from "../../atoms/Divider";
import dayjs from "dayjs";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import { useDeleteMonitorExclusion } from "../../../graphql/hooks/aml/useDeleteMonitorExclusion";

interface Props {
  parameter: Parameter;
  parameterExclusionOptions?: ParameterExclusionOption;
  monitoringEventId: string;
  clientIdentifier: string;
  refetch?: () => void;
  onCloseModal?: () => void;
}

const AmlMonitorException: React.FC<Props> = ({
  parameter,
  parameterExclusionOptions,
  monitoringEventId,
  clientIdentifier,
  refetch,
  onCloseModal,
}) => {
  const { t } = useTranslation();

  const [comment, setComment] = useState("");
  const [date, setDate] = useState(new Date());
  const [noExpirationDate, setNoExpirationDate] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const onCompletedCreateExclusion = (result: any) => {
    if (result?.aml?.createMonitorReferenceExclusion?.success) {
      setSuccessMessage(t("aml.exclusionCreatedSuccessfully"));
      setErrorMessage("");
    } else {
      setErrorMessage(t("aml.couldNotCreateExclusion"));
    }
  };
  const onErrorCreateExclusion = (e: any) => {
    setErrorMessage(e?.toString());
  };
  const { createMonitorExclusion, error, loading } = useCreateMonitorExclusion({
    onCompleted: onCompletedCreateExclusion,
    onError: onErrorCreateExclusion,
    skipRefetch: true,
  });

  const onClickMakeExclusion = async () => {
    try {
      await createMonitorExclusion({
        variables: {
          input: {
            comment: comment,
            expiresAt: noExpirationDate ? null : date,
            monitoringEventId: monitoringEventId,
            referenceIdentifier: parameter.id,
            clientIdentifier: clientIdentifier,
          },
        },
      });
    } catch (e) {
      setErrorMessage(e?.toString() ?? t("errors.somethingWentWrong"));
    }
  };

  const onError = (e: any) => {
    setErrorMessage(e?.toString());
  };
  const onCompleted = (result: any) => {
    if (result?.aml?.deleteMonitorReferenceExclusion?.success) {
      setSuccessMessage(t("aml.theExclusionHasBeenRemoved"));
      setErrorMessage("");
    } else {
      setErrorMessage(t("aml.theExclusionCouldNotBeRemoved"));
      setSuccessMessage("");
    }
  };
  const {
    deleteMonitorExclusion,
    error: deleteExclusionError,
    loading: deleteExclusionLoading,
  } = useDeleteMonitorExclusion({
    onError: onError,
    onCompleted: onCompleted,
    skipRefetch: true,
  });

  const onDeleteExclusion = async (exclusionId: string) => {
    try {
      await deleteMonitorExclusion({
        variables: {
          input: { id: exclusionId, clientIdentifier: clientIdentifier },
        },
      });
    } catch (error) {
      setErrorMessage(error?.toString() || t("errors.somethingWentWrong"));
    }
  };

  const onClickRefetch = () => {
    console.log("refetching...");
    refetch && refetch();
    onCloseModal && onCloseModal();
  };

  return (
    <div>
      <div>
        <HorizontalSpacer spacing={0.5} />
        <Divider />
        <HorizontalSpacer spacing={0.5} />
        <BodySmall fontweight={600} color={theme.colors.textSecondary}>
          {t("aml.exclusions")}
        </BodySmall>
        <HorizontalSpacer spacing={0.5} />
        {parameter.exclusions && parameter.exclusions.length > 0 && (
          <BodySmall color={theme.colors.info}>
            {t("aml.hasActiveExclusion")}
          </BodySmall>
        )}
        {parameter.exclusions.map((exclusion) => (
          <div key={exclusion.id}>
            <BodySmall>
              {t("aml.excluded")}:{" "}
              {dayjs(exclusion.createdAt).format("YYYY-MM-DD HH:mm")}
            </BodySmall>
            <BodySmall>
              {t("aml.endDate")}:{" "}
              {exclusion.activeUntilFurtherNotice
                ? t("aml.validUntilFurtherNotice")
                : dayjs(exclusion.activeUntil).format("YYYY-MM-DD HH:mm")}
            </BodySmall>
            <BodySmall>{exclusion.comment?.content}</BodySmall>
            <Row nogutter justify="end">
              <OutlinedButton
                title={t("aml.removeExclusion")}
                color={theme.colors.error}
                icon={<TrashIcon color={theme.colors.error} />}
                onClick={() => onDeleteExclusion(exclusion.id)}
                loading={deleteExclusionLoading}
              />
            </Row>
          </div>
        ))}
        {!parameter.exclusions ||
          (parameter.exclusions.length === 0 && (
            <div>
              <HorizontalSpacer spacing={0.5} />
              <Row nogutter justify="between" align="end">
                <div>
                  <BodySmall>{t("aml.validUntil")}</BodySmall>
                  <DatePicker
                    value={date}
                    onChange={(date) => setDate(date ?? new Date())}
                    disableDatesBeforeToday
                    maxDate={
                      parameterExclusionOptions?.latestAllowedExpirationDate
                    }
                  />
                </div>
                {!parameterExclusionOptions?.latestAllowedExpirationDate && (
                  <Checkbox
                    title={t("aml.validUntilFurtherNotice")}
                    checked={noExpirationDate}
                    onChange={() => setNoExpirationDate(!noExpirationDate)}
                  />
                )}
              </Row>
              <HorizontalSpacer spacing={0.5} />
              <TextField
                placeholder={t("aml.comment")}
                onChange={(value) => setComment(value)}
                value={comment}
                fullWidth
              />
              <HorizontalSpacer spacing={1} />
              <Row nogutter justify="end">
                <OutlinedButton
                  title={t("aml.createExclusion")}
                  color={theme.colors.primary}
                  disabled={comment.length < 3}
                  icon={<ClockIconSmall color={theme.colors.primary} />}
                  onClick={onClickMakeExclusion}
                  loading={loading}
                />
              </Row>
            </div>
          ))}
      </div>
      {(error || errorMessage || deleteExclusionError) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={errorMessage || error?.toString()}
        />
      )}
      {successMessage && (
        <Alert
          type="success"
          title={successMessage}
          message={
            <OutlinedButton
              title={t("aml.refresh")}
              onClick={onClickRefetch}
              color={theme.colors.primary}
            />
          }
        />
      )}
    </div>
  );
};

export default AmlMonitorException;
